// Contrato vinculante del Avivador v2. Tramo T3a (0.19.0-beta.2) y AMPLIADO en
// la fase 0 de T3b (0.19.0-beta.3): context/plan_build_avivador_v2.md, Ajustes
// #2, #3, #4, #7, #10 y #16, y las secciones E1 a E6 de T3. Lo leen los
// ejecutores en paralelo. Acá viven los TIPOS compartidos, la identidad de la
// entidad (`claveEntidad`), el contrato DOM y las claves de storage. Sin JSX
// (regla de Fast Refresh).
//
// T3b suma (sin romper T3a): vista Mosaico | Lista, orden por columna, Enter
// del buscador (tokens / popover), conteo del badge, persistencia por pestaña
// y aviso de novedades. Lo marcado «T4» NO se construye en T3b.
//
// T4a (fase 0, 4.0.0 → piloto con el Avivador v2 completo) suma, sin romper
// T3b: el PANEL lateral para resolver (modos acción y éxito), las MARCAS
// locales por `claveEntidad` (franja «Resuelta» con Deshacer / Corregir),
// «Siguiente →», `propios` en el aviso de novedades y la guarda de POST en
// vuelo. Las props de los componentes del panel, la franja, el velo y lo
// nuevo de PasoForm / EstadoRedesForm / paso-validacion viven en
// panel-avivador-tipos.ts. Lo marcado «T4b» (Posponer, Archivar, tecla P,
// marcas de pospuesta / archivada) NO se construye en T4a.
//
// T4b (fase 0 del orquestador, plan_migracion_reui.md §4.1 + Ajustes 20–26):
// suma los modos 'posponer' y 'archivar' del panel, `ResultadoPanel`
// (paso | posposición | archivo), las marcas 'pospuesta' y 'archivada' con
// su `MarcaRef`, `tipoMarca`, y en `EstadoAvivador` abrir(item, modo),
// setModo, posponer, archivar y posposicionCancelada. Posponer y archivar
// hacen el POST en el hook de la página (Ajuste 20: el componente del modo se
// desmonta al cerrar el panel y el `onSuccess` de un `mutate` suyo no correría).
//
// T5 (fase 0 del orquestador; plan_build_avivador_v2.md «T5 — Actividad,
// Comentar, cambio de versión y limpieza», A3; plan_migracion_reui.md §5):
// suma `ComentarioLocal`, `comentariosLocales` y `comentarioLocal(item, c)`
// en `EstadoAvivador` — el comentario que deja «Comentar» del panel (T5-B,
// panel-avivador-tipos.ts) sin esperar a «Actualizar». Las props del panel y
// los textos de Actividad / Comentar, y la firma de `ActividadLista`
// (rdt/actividad.tsx, T5-A), viven en panel-avivador-tipos.ts.
//
// Las piezas GENÉRICAS de filtros (botón, popover, chips, meta-row, facetas,
// tokens parametrizados, atajos / y Esc) viven en components/filtros/ (contrato
// en components/filtros/tipos.ts); acá solo lo del Avivador.
//
// Precedencia: context/design_avivador_v2.md §13 > §12 > §1–§11. La v2 NUNCA
// importa avivador-vm.ts, use-vidriera-state.ts ni
// use-avivador-filtros-persistidos.ts.
import type { ReactNode, RefObject } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ChipFiltro } from '@/components/filtros/tipos';
import type {
  AvivadorConfig,
  AvivadorItem,
  AvivadorRama,
  AvivadorUltimoComentario,
  EstadoRedes,
  TipoRdt,
} from '@/types/api';

// ---------------------------------------------------------------------------
// Identidad de la entidad (Ajuste #2)
// ---------------------------------------------------------------------------
// El `id` del ítem incluye la rama (`R3a:rdt:9`): resolver con una fecha vieja
// hace volver la MISMA rede con otro id (`R3b:rdt:9`). Todo estado que tiene
// que sobrevivir a un cambio de rama se indexa por la clave de la entidad:
// el foco guardado para volver (#8) en T3a; `propios`, las marcas y
// `siguiente` en T4a. El DOM y las `key` de React siguen con `item.id`.

/** `'rdt:9'` para una rede, `'obra:1426'` para una obra (R4). */
export type ClaveEntidad = `rdt:${number}` | `obra:${string}`;

/** Clave estable de la entidad del ítem: `'rdt:'+rdtId` si es una rede,
 *  `'obra:'+cc` si es una obra (R4). Una rede sin `rdtId` no existe en la API;
 *  si llegara, cae a la clave de su obra. IMPLEMENTADA (no es stub). */
export function claveEntidad(item: Pick<AvivadorItem, 'entidadTipo' | 'rdtId' | 'cc'>): ClaveEntidad {
  if (item.entidadTipo === 'rdt' && item.rdtId != null) return `rdt:${item.rdtId}`;
  return `obra:${item.cc}`;
}

// ---------------------------------------------------------------------------
// Filtros de la cola (view-model puro en avivador-filtros.ts)
// ---------------------------------------------------------------------------
// FiltrosCola tiene el shape COMPLETO desde T3a. En T3b la UI mueve TODOS los
// campos: el popover (vida, ramas, encargada, tipos, orden), el Enter del
// buscador (tokens; rama y Control RDT van al popover), las columnas de la
// Lista (orden + ordenDir) y el buscador en vivo (q).

/** Mostrar (§13 2.1): abre en Vivas. */
export type VidaFiltro = 'vivas' | 'olvidadas' | 'todas';

/** Orden de la cola. `prioridad` = el orden del server (ya aplica
 *  `config.ordenRamas`). El popover ofrece prioridad · dias · obra · cliente;
 *  las columnas de la Lista, rede · obra · dias · estado · cliente · encargada
 *  (Control RDT; la clave queda 'encargada' por compatibilidad con T3a). Es UN
 *  solo estado: el chip «Orden: … ↑/↓», el radio del popover y la flecha de la
 *  columna leen lo mismo (§13 9.1). */
export type OrdenClave = 'prioridad' | 'dias' | 'obra' | 'cliente' | 'rede' | 'estado' | 'encargada';
export type OrdenDir = 'asc' | 'desc';

/** Dirección con la que ENTRA cada orden (popover o clic en otra columna):
 *  Demora («Más demoradas primero») desc, el resto asc (M:541-545, M:572).
 *  IMPLEMENTADA (no es stub). */
export const ORDEN_DIR_NATURAL: Readonly<Record<OrdenClave, OrdenDir>> = {
  prioridad: 'asc',
  dias: 'desc',
  obra: 'asc',
  cliente: 'asc',
  rede: 'asc',
  estado: 'asc',
  encargada: 'asc',
};

/** Campo de un token del buscador (Enter). Rama y Control RDT NO son tokens:
 *  Enter los vuelca al filtro del popover (DP-5; relevamiento §14.6). */
export type TokenField = 'obra' | 'cliente' | 'estadoObra' | 'text';

/** Categoría visible del chip de un token, en negrita (Ajuste #16: «Estado de
 *  obra», nunca «Estado» a secas). '' = texto libre (sin categoría).
 *  @deprecated 'Estado': solo por compatibilidad del test de T3a
 *  (avivador-filtros.test.ts, `tok(... cat: 'Estado' ...)`). Ningún código
 *  nuevo lo crea; `parseFiltrosPersistidos` lo rechaza. F2 pasa el test a
 *  'Estado de obra' y el orquestador lo saca del union en la integración. */
export type CatToken = '' | 'Obra' | 'Cliente' | 'Estado de obra' | 'Estado';

/** `cat` que corresponde a cada `field` (el único válido). IMPLEMENTADA. */
export const CAT_POR_CAMPO: Readonly<Record<TokenField, Exclude<CatToken, 'Estado'>>> = {
  obra: 'Obra',
  cliente: 'Cliente',
  estadoObra: 'Estado de obra',
  text: '',
};

export interface TokenCola {
  field: TokenField;
  /** `obra` → cc exacto; `cliente` / `estadoObra` → valor crudo del ítem;
   *  `text` → `normalizarBusqueda(texto)` (components/filtros/tokens.ts). */
  value: string;
  /** Siempre `CAT_POR_CAMPO[field]`. */
  cat: CatToken;
  /** Lo que muestra el chip: el cc («1426», también cuando se detectó por el
   *  nombre de la obra), el cliente, el estado o el texto como se tipeó (trim). */
  label: string;
}

/** Valor de `FiltrosCola.encargada` que significa «Todo el equipo». */
export const TODO_EL_EQUIPO = 'Todas';

export interface FiltrosCola {
  vida: VidaFiltro;
  /** Selección múltiple; [] = todas las ramas. Chips del popover (orden del ciclo). */
  ramas: AvivadorRama[];
  /** Código de Control RDT ('A. Vera' | 'A. Altamirando' | 'M. Ferreira') o `TODO_EL_EQUIPO`. */
  encargada: string;
  /** Selección múltiple; [] = los dos tipos. R4 (tipo null) sale si hay algún tipo elegido (DP-6). */
  tipos: TipoRdt[];
  /** AND entre tokens, en orden de alta. Los crea Enter en el buscador. */
  tokens: TokenCola[];
  orden: OrdenClave;
  ordenDir: OrdenDir;
  /** Texto del buscador TAL CUAL se tipea (sin normalizar, sin debounce). La
   *  lista se filtra con `normText(qDebounced).trim()` (Ajuste #10). Se
   *  persiste con el resto (DP-14). */
  q: string;
}

/** Conteos cruzados (M:477-500 del preview): cada filtro cuenta contra todos
 *  los demás, nunca contra sí mismo. */
export interface FacetasCola {
  /** Sobre `base ∩ ramas` (sin recortar por vida). */
  vida: Record<VidaFiltro, number>;
  /** `n` = base ∩ vida en esa rama; `total` = base en esa rama (sin vida). */
  ramas: Record<AvivadorRama, { n: number; total: number }>;
  /** Sobre `base0 ∩ vida ∩ ramas` (sin el filtro de Control RDT), por código.
   *  Incluye la clave `TODO_EL_EQUIPO` con el total. Los ítems sin Control RDT
   *  solo cuentan en `TODO_EL_EQUIPO`. */
  encargadas: Record<string, number>;
}

export interface ResultadoFiltro {
  /** Filtrada, en el orden de entrada (prioridad del server). El orden elegido
   *  lo aplica después `ordenarLista` (la página: `ordenarLista(filtrarCola(…).lista, …)`). */
  lista: AvivadorItem[];
  facetas: FacetasCola;
}

/** Identificador de un chip de filtro activo (meta-row, §13 1.3).
 *  - `tok:N`: token N del buscador (índice en `tokens`).
 *  - `vida`, `rama:R*`, `encargada`, `tipo:*`, `orden`: los del popover.
 *  - `q`: el texto en vivo del buscador (T3a). En T3b la página YA NO lo pide
 *    (`chipsActivos(f)` sin `textoEnVivo`: el preview no tiene chip del texto
 *    en vivo y no suma al badge). Queda en el tipo por compatibilidad. */
export type ChipKey =
  | `tok:${number}`
  | 'vida'
  | `rama:${AvivadorRama}`
  | 'encargada'
  | `tipo:${TipoRdt}`
  | 'orden'
  | 'q';

/** Un chip de filtro activo del Avivador = el `ChipFiltro` genérico de
 *  components/filtros con las claves de acá. `cat` sin los dos puntos
 *  («Control RDT», «Rama», «Obra», «Cliente», «Estado de obra», «Tipo»,
 *  «Orden»); null = se lee solo (Mostrar, texto libre). `ariaLabel`: «Quitar
 *  Control RDT Ana Vera», «Quitar búsqueda metrobus», «Quitar rama Sin entregar»… */
export type ChipActivo = ChipFiltro<ChipKey>;

export interface OpcionesChips {
  /** T3a: el texto del buscador YA con debounce. Si no está vacío (trim), se
   *  suma el chip `{key:'q', cat:null, label: texto recortado}` justo después
   *  de los tokens. En T3b la página deja de pasarlo. Omitido = sin chip de
   *  búsqueda. */
  textoEnVivo?: string;
}

// ---------------------------------------------------------------------------
// Enter del buscador (T3b; detectarEnter / aplicarEnter en avivador-filtros.ts)
// ---------------------------------------------------------------------------

/** Qué hizo el Enter (§13 2.5, DP-5, DP-7). Rama y Control RDT se vuelcan al
 *  popover (una sola fuente: nada de tokens duplicados ni AND imposible). */
export type ResultadoEnter =
  /** Texto vacío (solo espacios): no pasa nada. */
  | { kind: 'vacio' }
  /** Obra (por CC o por nombre único), cliente, estado de obra o texto libre. */
  | { kind: 'token'; token: TokenCola }
  /** «sin entregar», «R3a» → se suma a `ramas`. */
  | { kind: 'rama'; rama: AvivadorRama }
  /** «vera», «A. Vera», «Ana Vera» → reemplaza `encargada` (el código). */
  | { kind: 'encargada'; codigo: string };

// ---------------------------------------------------------------------------
// Novedades (§13 3.3(a), Ajustes #2 y #13; contarCambios en avivador-filtros.ts)
// ---------------------------------------------------------------------------

/** Diferencia entre la foto congelada y la fresca, por `claveEntidad` (Map
 *  clave → rama; una entidad está en una sola rama a la vez). */
export interface CambiosCola {
  /** Claves que entran + claves que salen + claves que cambian de rama, sin
   *  las de `propios` (§13 3.3(a)). `propios` = claves con marca vigente ∪
   *  claves deshechas hasta el próximo «Actualizar» ∪, por cada marca de
   *  estado de redes Archivado / No Aplica, todas las entidades de esa obra en
   *  la foto congelada y en la fresca (`calcularPropios`). Las marcas de
   *  pospuesta y archivada (T4b) son marcas: tampoco cuentan. T5: las claves
   *  de `comentariosLocales` TAMBIÉN entran a `propios` (comentar la propia
   *  rede no es un cambio ajeno), aunque no sean una marca. */
  total: number;
  /** De las que salen o cambian de rama, cuántas estaban en la vista (la
   *  lista visible con los filtros actuales): «Otras personas del equipo
   *  resolvieron N redes de esta vista». Nunca > `total`. */
  resueltasEnVista: number;
}

/** Textos del aviso (textosNovedades en avivador-filtros.ts). */
export interface TextosNovedades {
  /** «3 cambios desde que entraste.» / «1 cambio desde que entraste.» (miles es-AR). */
  titulo: string;
  /** «Otras personas del equipo resolvieron 3 redes de esta vista.» / «Otra
   *  persona del equipo resolvió 1 rede de esta vista.»; null si `resueltasEnVista` es 0. */
  otros: string | null;
}

/** El aviso de novedades en el estado de la página (se muestra en Mosaico y en
 *  Lista, DP-17; solo con `count > 0`). */
export interface NovedadesAvivador {
  /** `contarCambios(fotoCongelada.items, fotoFresca.items, propios, lista).total`. 0 = sin aviso. */
  count: number;
  titulo: string;
  textoOtros: string | null;
  /** «Actualizar»: `useAvivador().aplicarNovedades()`. La foto nueva cambia
   *  `layoutKey` (generadoAt), así que el reparto va con FLIP (§13 3.3(b)). */
  aplicar(): void;
}

// ---------------------------------------------------------------------------
// Vista y persistencia (T3b, §13 2.4; relevamiento de la página §11)
// ---------------------------------------------------------------------------

export type VistaAvivador = 'mosaico' | 'lista';

/** localStorage (queda entre pestañas y recargas): 'mosaico' | 'lista'. */
export const AVIVADOR_VISTA_STORAGE_KEY = 'redeapp.avivador.vista';

/** sessionStorage (por pestaña): `serializarFiltros(filtros)`, TODO
 *  `FiltrosCola` incluido `q` (DP-14). Se lee con `parseFiltrosPersistidos`.
 *  Reemplaza a 'redeapp.avivador.filtros.v1' de la v1 (que no se toca). */
export const AVIVADOR_FILTROS_V2_STORAGE_KEY = 'redeapp.avivador.filtros.v2';

/** Vista leída del storage: 'lista' solo si es exactamente 'lista'; cualquier
 *  otra cosa (null, basura) → 'mosaico'. IMPLEMENTADA (no es stub). */
export function parseVista(raw: string | null): VistaAvivador {
  return raw === 'lista' ? 'lista' : 'mosaico';
}

// ---------------------------------------------------------------------------
// Contexto de cálculo de la card y las urgencias
// ---------------------------------------------------------------------------

export interface CtxAvivador {
  /** 'YYYY-MM': `AvivadorResponse.periodoHabilitado`. */
  periodoHabilitado: string;
  /** `config.diaD`. */
  diaD: number;
  /** 'YYYY-MM-DD' en hora AR (UTC−3) de `AvivadorResponse.generadoAt`. Toda
   *  cuenta de días de la página usa ESTE hoy, no el reloj del navegador. */
  hoy: string;
}

// ---------------------------------------------------------------------------
// Presentación por rama (E1 lo implementa en avivador-ramas.ts)
// ---------------------------------------------------------------------------

/** Color de la urgencia: ámbar = `text-warning`, rojo = `text-destructive`,
 *  gris = `text-muted-foreground` (ver `TONO_TEXTO_CLASE`). */
export type Tono = 'ambar' | 'rojo' | 'gris';

export interface Urgencia {
  /** «vencido hace 4 días», «alta hace 12 días», «hace 1.091 días», «sin fecha», «dato del maestro». */
  texto: string;
  tono: Tono;
  /** Texto del Tooltip de los días (§13 5.4). null = sin tooltip (R4, «sin fecha»). */
  tooltip: string | null;
  /** `stroke-dashoffset` del arco (0 = lleno, `ARCO_CIRCUNFERENCIA` = vacío).
   *  Solo R3 con umbral y días; null = sin arco. */
  arco: number | null;
}

export interface RamaMeta {
  /** «Chequeo vencido»… (= RAMA_LABEL). */
  label: string;
  /** Acción del panel y del aria de la card: «Registrar chequeo», «Informar
   *  monto», «Registrar entrega», «Registrar aprobación», «Marcar facturado»,
   *  «Definir estado de redes». */
  accion: string;
  /** Ícono Lucide de la etiqueta de rama (13 px, trazo 2). */
  Icon: LucideIcon;
  /** Bajada de la franja resuelta (T4): «chequeo {mmm-aa} registrado», «monto
   *  informado»… Solo R1 usa el período; con null, «chequeo registrado». */
  subResuelta: (periodo: string | null) => string;
  /** Título de la caja verde (T4): «Chequeo registrado», «Monto informado»… */
  tituloExito: string;
}

/** Estado de un mes de la tira de R1. */
export type EstadoMesTira = 'sin-chequeo' | 'no-salto' | 'salto' | 'a-chequear';

export interface MesTira {
  /** 'YYYY-MM'. */
  periodo: string;
  /** 'mmm-aa' (perLabel). */
  etiqueta: string;
  estado: EstadoMesTira;
  /** Tooltip y aria-label del mes: «ago-26: no saltó · 1,92 %», «jul-26: saltó ·
   *  4,31 %» (sin % si falta), «sep-26: a chequear», «jun-26: sin chequeo registrado». */
  tooltip: string;
}

/** Paso del ciclo estático de la card R3 (4 pasos). */
export interface PasoCiclo {
  /** Color del paso (claves de STEP_COLOR_CLASS / CONN_DONE_CLASS). */
  color: 'salto' | 'entrega' | 'aprobado' | 'facturado';
  /** «Saltó» (Provisoria) o «Calculado» (Definitiva) · «Entrega» · «Aprobado» · «Facturado». */
  etiqueta: string;
  estado: 'hecho' | 'proximo' | 'pendiente';
  /** Fecha dd/mm/aa, «per. mmm-aa», «sin fecha», «lo que sigue» (el próximo) o '' (pendientes). */
  sub: string;
  /** Conector hacia el paso de la derecha: 'hecho' si el de la derecha está
   *  hecho (color del paso izquierdo al 55 %), 'pendiente' si no, null en el último. */
  conector: 'hecho' | 'pendiente' | null;
}

/** Un dato («hecho») de la grilla de 2 columnas de la card (R1, R2 y R4). */
export interface HechoCard {
  clave: string;
  valor: string;
  /** normal = foreground · muted = terciario 500 · warn = text-warning · salta = text-salta. */
  tono: 'normal' | 'muted' | 'warn' | 'salta';
}

// ---------------------------------------------------------------------------
// Datos congelados (Ajuste #3) — decidirCongelado en avivador-congelado.ts (re-exportado por avivador-filtros.ts)
// ---------------------------------------------------------------------------

export interface EntradaCongelado {
  /** `query.data !== undefined`. */
  tieneDatos: boolean;
  /** `query.isFetchedAfterMount` de TanStack. */
  isFetchedAfterMount: boolean;
  /** `query.isStale` de TanStack (una invalidación lo pone en true). */
  isStale: boolean;
  /** Ya hay una foto congelada: lo que llega después va al aviso de novedades (T3b). */
  yaCongelado: boolean;
}

// ---------------------------------------------------------------------------
// Volver a la página (#8, Ajuste #3)
// ---------------------------------------------------------------------------

/** sessionStorage: foco para restaurar al volver de la rede. Se lee UNA vez y
 *  se borra. Reemplaza a 'redeapp.avivador.focoId' de la v1 (que no se toca). */
export const AVIVADOR_FOCO_V2_STORAGE_KEY = 'redeapp.avivador.foco.v2';

/** localStorage: flag de la v2 ('1' = mosaico). `?mosaico=1` lo guarda,
 *  `?mosaico=0` lo borra (plan §1.1). */
export const AVIVADOR_MOSAICO_STORAGE_KEY = 'redeapp.avivador.mosaico';

export interface FocoGuardadoV2 {
  /** Entidad de la card que se abrió. */
  clave: ClaveEntidad;
  /** Entidad de la card que la seguía en la lista visible al salir (null si era la última). */
  claveSiguiente: ClaveEntidad | null;
  /** `card.getBoundingClientRect().top − main.getBoundingClientRect().top` al
   *  salir: al volver, la card queda a la misma altura de la ventana. */
  offset: number;
  /** `main.scrollTop` al salir: se usa solo si no aparece ni la card ni la siguiente. */
  scrollTop: number;
}

// ---------------------------------------------------------------------------
// Panel, resultado de un paso y marcas locales (T4a; §13 ciclo 15, 7.2, 8.1,
// 8.2; plan T4 fase 0 + Ajustes #2, #6 y #8)
// ---------------------------------------------------------------------------

/** Modo del panel. 'accion' = el form de la rama («+ Paso» en variante
 *  avivador, o EstadoRedesForm en R4); 'exito' = la caja con lo que quedó
 *  (§13 8.1: la verde de un paso; T4b: también el resumen de una posposición o
 *  de un archivo); 'posponer' y 'archivar' = los modos del pie (§13 7.4 y 10.4;
 *  'archivar' solo en redes, nunca en R4 ni en una entidad obra). */
export type PanelModo = 'accion' | 'exito' | 'posponer' | 'archivar';

/** Los modos que se piden a mano (pie, tecla P, «Cancelar» de un modo). */
export type ModoPedido = 'accion' | 'posponer' | 'archivar';

/**
 * Lo que devuelve el form cuando el POST salió bien (`onRegistrado` de
 * PasoForm / EstadoRedesForm, llamado en el `onSuccess` de la mutation, con
 * los datos de la RESPUESTA o de las variables enviadas; nunca se deduce
 * mirando la rede después, como la v1):
 * - chequeo: `id` = el `id` que devuelve `useCrearChequeo` (el chequeo creado);
 *   `periodo` 'YYYY-MM' y `salta` = lo enviado.
 * - monto: `id` = el `id` que devuelve `useCrearMonto` (el monto creado).
 * - hito: `codigo` = el código ENVIADO a `useCompletarHito` ('entregado_obra' |
 *   'entregado_cliente' | 'aprobado' | 'poliza_ec_enviada' | 'facturado').
 *   `polizaAgregada` (solo 'aprobado'): true si se mandó `polizaAplica: true`
 *   y la rede NO tenía la póliza EC elegida antes del POST (el backend la
 *   agregó en el mismo POST). El Deshacer de esa aprobación, además de
 *   `useDeshacerHito`, la saca con `useActualizarRdt({ hitos: { polizaAplica:
 *   false } })`: el backend des-completa la aprobación pero deja la póliza
 *   pedida. Omitido = false (la póliza ya estaba, o no se pidió).
 * - estado-redes: el estado elegido y la nota enviada (null si iba vacía).
 */
export type ResultadoPaso =
  | { tipo: 'chequeo'; id: number; periodo: string; salta: boolean }
  | { tipo: 'monto'; id: number }
  | { tipo: 'hito'; codigo: string; polizaAgregada?: boolean }
  | { tipo: 'estado-redes'; estado: EstadoRedes; nota: string | null };

/** Lo que dejó el panel sobre una entidad (T4b): un paso (T4a), una
 *  posposición (`id` y `hasta` de la respuesta de POST /api/avivador/posponer;
 *  `nota` la enviada) o un archivo (`nota` la enviada, null si iba vacía). */
export type ResultadoPanel =
  | ResultadoPaso
  | { tipo: 'posposicion'; id: number; hasta: string /* YYYY-MM-DD */; nota: string }
  | { tipo: 'archivo'; nota: string | null };

/**
 * Qué se registró, para saber qué hace el botón de la franja y de la caja
 * verde (`accionFranja`). Cada acción usa un endpoint auditado que YA existe:
 * - chequeo → «Corregir» = ir a la rede, donde se corrige (DP-19). Un chequeo
 *   no se deshace desde el Avivador.
 * - monto → «Deshacer» = `useBorrarMonto({ cc, id: rdtId, mid })`.
 * - hito → «Deshacer» = `useDeshacerHito({ cc, id: rdtId, codigo })` (el
 *   backend solo deshace el ÚLTIMO hito: es el recién registrado).
 * - estado-redes → sin botón (§13 8.2: «sin botón en Sin estado de redes»).
 * - posposicion (T4b) → «Deshacer» = `useCancelarPosposicion(id)`; NO exige
 *   `rdtId` (una obra R4 también se pospone; Ajuste 21). 404 / 409 = ya
 *   cancelada en otro lado → éxito silencioso.
 * - archivo (T4b) → «Deshacer» = `useArchivarRdt({ cc, id: rdtId, archivar:
 *   false })`. 400 «no está archivada» con el detalle fresco sin archivar = éxito
 *   silencioso; 409 (regla 3: otra rede activa con el mismo nro) = toast rojo con
 *   el mensaje y la marca queda (Ajuste 22).
 */
export type MarcaRef =
  | { kind: 'chequeo'; chid: number }
  | { kind: 'monto'; mid: number }
  | { kind: 'hito'; codigo: string }
  | { kind: 'estado-redes' }
  | { kind: 'posposicion'; id: number; hasta: string }
  | { kind: 'archivo' };

/**
 * Marca local de una entidad resuelta desde el panel (§13 8.2). Vive en
 * memoria en `EstadoAvivador.marcas` (Map por `claveEntidad`, Ajuste #2):
 * sobrevive a filtros, orden, búsqueda y «Actualizar» (Ajuste #6) y se pierde
 * al desmontar la página («el Deshacer vive hasta volver a entrar»).
 */
export interface MarcaLocal {
  /** `tipoMarca(resultado)`: un paso → 'resuelta'; una posposición →
   *  'pospuesta'; un archivo → 'archivada'. */
  tipo: 'resuelta' | 'pospuesta' | 'archivada';
  ref: MarcaRef;
  /** Bajada de la franja: `textosResultado(resultado).sub` (panel-avivador-tipos.ts),
   *  p. ej. «chequeo ago-26 registrado», «entrega registrada». */
  sub: string;
  /** Índice de prioridad (orden del server) del ítem en la población que se
   *  mostraba al marcarlo (`mapaPrioridad` de la población compuesta). Con
   *  «Actualizar», la entidad marcada se reinserta en ese índice aunque el
   *  server ya no la mande o la mande en otra rama (Ajuste #6). */
  posicion: number;
  /** Foto del ítem al marcarlo. La franja, la fila de la Lista y los filtros
   *  usan ESTA foto (la entidad no se mueve de rama ni de lugar hasta volver a
   *  entrar), aunque la foto aplicada del server traiga otra cosa. */
  item: AvivadorItem;
  /** Lo que devolvió el form (o la posposición / el archivo): reabrir la
   *  franja (click / Enter) muestra el panel en modo 'exito' con esto. */
  resultado: ResultadoPanel;
}

/** El panel abierto (null = cerrado). */
export interface PanelAbierto {
  /** El ítem sobre el que se abrió (con marca: `marca.item`). */
  item: AvivadorItem;
  modo: PanelModo;
  /** Solo en 'exito': lo registrado (o `marca.resultado` al reabrir una franja). */
  resultado?: ResultadoPanel;
}

/** Qué ofrece la franja / la caja verde para una marca. */
export type AccionMarca = 'deshacer' | 'corregir' | null;

/** `MarcaRef` de un resultado (lo usa `registrarExito`). IMPLEMENTADA (no es stub). */
export function refDesdeResultado(r: ResultadoPanel): MarcaRef {
  switch (r.tipo) {
    case 'posposicion':
      return { kind: 'posposicion', id: r.id, hasta: r.hasta };
    case 'archivo':
      return { kind: 'archivo' };
    case 'chequeo':
      return { kind: 'chequeo', chid: r.id };
    case 'monto':
      return { kind: 'monto', mid: r.id };
    case 'hito':
      return { kind: 'hito', codigo: r.codigo };
    case 'estado-redes':
      return { kind: 'estado-redes' };
  }
}

/** Tipo de marca de un resultado: posposición → 'pospuesta'; archivo →
 *  'archivada'; un paso → 'resuelta'. IMPLEMENTADA (no es stub). */
export function tipoMarca(r: ResultadoPanel): MarcaLocal['tipo'] {
  if (r.tipo === 'posposicion') return 'pospuesta';
  if (r.tipo === 'archivo') return 'archivada';
  return 'resuelta';
}

/** Botón de la franja, de la fila marcada y de la caja verde: chequeo →
 *  'corregir' (R1, DP-19); monto / hito → 'deshacer' (R2 y R3); estado-redes →
 *  null (R4, sin botón). T4b: posposición y archivo → 'deshacer'.
 *  IMPLEMENTADA (no es stub). */
export function accionFranja(ref: MarcaRef): AccionMarca {
  switch (ref.kind) {
    case 'chequeo':
      return 'corregir';
    case 'monto':
    case 'hito':
    case 'posposicion':
    case 'archivo':
      return 'deshacer';
    case 'estado-redes':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Comentario local (T5; plan_build_avivador_v2.md «T5 — Actividad, Comentar…»
// A3; plan_migracion_reui.md §5)
// ---------------------------------------------------------------------------
// «Comentar» del panel (panel-avivador-tipos.ts, `PanelAvivadorActividad`)
// deja registro sin resolver la rede: no es una marca (no pasa a franja, no
// cambia de rama ni de `posicion`), pero SÍ tiene que verse al toque en la
// card y en la fila de la Lista, sin esperar a «Actualizar» — igual criterio
// de identidad que las marcas (Ajuste #2: Map por `claveEntidad`) y de
// permanencia que Ajuste #6 (sobrevive a filtros, orden, búsqueda y
// «Actualizar»; se pierde SOLO al desmontar la página).

/**
 * Lo que dejó un «Comentar» sobre una entidad, sin esperar a «Actualizar».
 * Vive en `EstadoAvivador.comentariosLocales` (Map por `claveEntidad`, Ajuste
 * #2). A diferencia de `MarcaLocal`, NO es una marca: la entidad NO pasa a
 * franja ni cambia de rama ni de `posicion` — sigue exactamente donde
 * estaba; solo cambia lo que se pinta en su lugar (Ajuste plan T5 A3: «solo
 * se recoloca su columna», el FLIP de esa columna si el alto de la card
 * cambió, nunca un reparto entero).
 */
export interface ComentarioLocal {
  /** Reemplaza a `item.ultimoComentario` en la card y en la fila de la Lista
   *  mientras la clave siga en el mapa. Mismo shape que ya pintan las dos
   *  (`AvivadorUltimoComentario`): lo arma `PanelAvivadorActividad` con el
   *  `id` de la respuesta de `useCrearComentario`, el texto tipeado, el autor
   *  de `useMe()` y la hora local (`editadoAt: null`). */
  ultimoComentario: AvivadorUltimoComentario;
  /** Cuántos «Comentar» locales se sumaron sobre `item.comentariosCount`
   *  desde que se congeló la foto: ≥ 1, y SUMA (no se reemplaza por 1) si la
   *  misma entidad recibe más de un comentario en la misma visita sin volver
   *  a entrar. El conteo mostrado es `item.comentariosCount + delta`. */
  delta: number;
}

// ---------------------------------------------------------------------------
// Contrato DOM
// ---------------------------------------------------------------------------
// - El elemento ENFOCABLE de cada ítem lleva `data-avivador-item={item.id}`:
//   en el Mosaico, el `<article>` de la card; en la Lista (T3b), el `<button>`
//   de la celda Rede (el único tab stop de la fila; sin `onClick` propio: el
//   click lo toma la fila y Enter el listener global, Ajuste #7). Es el
//   destino de ←/→, Enter, O y de la restauración del foco (#8). Ningún otro
//   descendiente de la card o de la fila es tab stop.
// - Cada slot del mosaico es `div[data-mosaico-slot][data-mosaico-id={item.id}]`
//   con `key={item.id}` (E2). La card lleva además `data-mosaico-card`.
// - La fila de la Lista lleva `data-lista-fila={item.id}`; la restauración del
//   #8 mide la fila (no el botón) cuando no hay slot del mosaico.
// - T4a, FRANJA (Ajuste #8): su raíz es `<div data-mosaico-franja
//   data-avivador-item={item.id} tabIndex={-1} role="status">` (destino de
//   ←/→, de Enter y del `finalFocus` del panel, pero NO tab stop). El
//   `data-avivador-item` va en el contenedor y NUNCA en su botón: con el foco
//   en un elemento con ese atributo, Enter lo toma el listener (abrir) y el
//   botón no se activaría. Sus botones (Deshacer / Corregir) sí son tab stops,
//   la única excepción a «un solo tab stop por ítem». Lo mismo en la fila
//   marcada de la Lista: su botón Deshacer / Corregir es un segundo tab stop.
// - T4a, PANEL: el popup lleva `data-avivador-panel` (y `data-modo={modo}`);
//   el velo, `data-avivador-velo`. Los usa el harness (QA-4).
// - Nada de selectores por id (los ids tienen «:»): siempre por atributo, con
//   `selectorAvivadorItem`.

export const ATTR_AVIVADOR_ITEM = 'data-avivador-item';
/** Raíz de la franja resuelta del mosaico (T4a). */
export const ATTR_MOSAICO_FRANJA = 'data-mosaico-franja';
/** Popup del panel lateral (T4a). */
export const ATTR_AVIVADOR_PANEL = 'data-avivador-panel';
/** Velo del panel (T4a). */
export const ATTR_AVIVADOR_VELO = 'data-avivador-velo';
/** Todos los ítems enfocables de la página, en orden de DOM (= prioridad). */
export const SELECTOR_AVIVADOR_ITEMS = '[data-avivador-item]';

/** Selector del ítem enfocable con ese `item.id` (atributo entre comillas;
 *  escapa `"` y `\`). IMPLEMENTADA (no es stub). */
export function selectorAvivadorItem(id: string): string {
  return `[${ATTR_AVIVADOR_ITEM}="${id.replace(/["\\]/g, '\\$&')}"]`;
}

// ---------------------------------------------------------------------------
// Estado de la página (F4 lo arma en use-avivador-estado.ts; los controles, la
// meta-row, la Lista y la página lo consumen)
// ---------------------------------------------------------------------------

export interface EstadoAvivador {
  /** Todavía no hay foto congelada (primer fetch o esperando datos frescos, Ajuste #3): skeleton. */
  cargando: boolean;
  /** Falló la carga y no hay foto: `ErrorCargaAvivador`. */
  error: boolean;
  reintentar(): void;
  /** null mientras carga. */
  ctx: CtxAvivador | null;
  config: AvivadorConfig | null;
  /** `data.pospuestos` (link «N pospuestas» de la meta-row). */
  pospuestos: AvivadorItem[];
  /** M de «Mostrando N … de M redes» = largo de la POBLACIÓN (sin filtros, sin
   *  pospuestos). T4a: la población es la COMPUESTA (ver `lista`), así una
   *  franja reinsertada después de «Actualizar» también cuenta. */
  total: number;

  /** Filtros vigentes. Al montar: lo persistido en la pestaña
   *  (`AVIVADOR_FILTROS_V2_STORAGE_KEY` + `parseFiltrosPersistidos`); si no hay,
   *  `filtrosIniciales(data.encargadaInicial)` cuando llegan los datos. */
  filtros: FiltrosCola;
  /** Cambia campos sueltos (popover, vacíos). El orden desde el popover entra
   *  con su dirección natural: `setFiltros({ orden: c, ordenDir: ORDEN_DIR_NATURAL[c] })`. */
  setFiltros(patch: Partial<FiltrosCola>): void;
  /** `filtros.q` con debounce de 250 ms. `lista` y `layoutKey` usan ESTE valor. */
  qDebounced: string;
  /** Lo que se pinta (Mosaico y Lista), filtrado Y ordenado:
   *  `ordenarLista(filtrarCola(data.items, f', normText(qDebounced).trim()).lista,
   *  mapaPrioridad(data.items), f'.orden, f'.ordenDir)`, con `f' = { ...filtros, q: qDebounced }`.
   *  Con `orden: 'prioridad'` queda el orden del server (§13 3.2).
   *  T4a (Ajuste #6): en vez de `data.items` se parte de la POBLACIÓN
   *  COMPUESTA con las marcas: de la foto aplicada se sacan los ítems cuya
   *  `claveEntidad` tiene marca y se inserta cada `marca.item` en
   *  `min(marca.posicion, largo)`, en orden ascendente de `posicion`. Antes de
   *  «Actualizar» da la misma población; después, la franja queda en su lugar
   *  aunque el server ya no la mande o la mande en otra rama. `mapaPrioridad`,
   *  `filtrarCola`, las facetas y `total` usan esa población compuesta. */
  lista: AvivadorItem[];
  facetas: FacetasCola;
  /** `chipsActivos(f')` SIN `textoEnVivo` (T3b: el texto en vivo no tiene chip). */
  chips: ChipActivo[];
  /** Badge de «Filtros» = `contarFiltros(filtros)`: (vida ≠ vivas) + ramas +
   *  (Control RDT ≠ Todo el equipo) + tipos + tokens. El orden y el texto en
   *  vivo NO suman; el Control RDT inicial de la persona SÍ (se ve «1» al entrar). */
  nFiltros: number;
  /** `filtros.tokens.length`: la línea «+ N de la búsqueda» del popover (Ajuste #16). */
  tokensBusqueda: number;
  /** «Limpiar» del popover habilitado: `nFiltros > 0 || filtros.orden !== 'prioridad' || filtros.q.trim() !== ''`. */
  puedeLimpiar: boolean;
  /** `setFiltros(quitarChip(filtros, k))`; con `'q'`, `qDebounced` en '' al
   *  instante. SOLO estado: el foco lo mueve `MetaFiltros` (components/filtros),
   *  que es quien sabe que el chip se quitó desde la fila. */
  quitarChip(k: ChipKey): void;
  /** «Limpiar todo» (meta-row), «Limpiar» (popover) y «Limpiar filtros» (vacío):
   *  `limpiarFiltros()` (Vivas, Todo el equipo, prioridad asc, sin tokens,
   *  buscador vacío) y `qDebounced` en '' al instante. SOLO estado (desde el
   *  popover el foco queda en el popover; desde la meta-row lo mueve MetaFiltros). */
  limpiarTodo(): void;
  /** Enter en el buscador: `aplicarEnter(filtros, detectarEnter(texto, data.items))`
   *  (se detecta contra la POBLACIÓN, no contra la lista filtrada). Si el
   *  resultado no es 'vacio', el buscador queda vacío y `qDebounced` en '' al
   *  instante (el chip nuevo reemplaza al texto). */
  enterBuscador(texto: string): void;
  /** Clic en el encabezado de una columna de la Lista:
   *  `setFiltros(clicColumna(filtros.orden, filtros.ordenDir, c))`. */
  clicColumna(c: OrdenClave): void;
  /** Códigos de Control RDT para los chips del popover (después de «Todo el
   *  equipo»): los de `ENC_NOMBRES` siempre (aunque cuenten 0, como las
   *  facetas) más cualquier otro presente en la población, ordenados por nombre. */
  encargadas: string[];
  /** Cambia SOLO por una acción propia: `vista|filtros sin q|normText(qDebounced).trim()|generadoAt
   *  de la foto aplicada` (el orden va dentro de los filtros). Le dice al
   *  Mosaico que ese reparto va con FLIP (Ajuste #4). */
  layoutKey: string;

  /** Mosaico | Lista. Se lee UNA vez al montar (`parseVista` de
   *  localStorage[`AVIVADOR_VISTA_STORAGE_KEY`], sin esperar los datos). */
  vista: VistaAvivador;
  /** Cambia la vista y la guarda en localStorage. La tecla L alterna. */
  setVista(v: VistaAvivador): void;

  /** Aviso «N cambios desde que entraste · Actualizar» (Mosaico y Lista, DP-17). */
  novedades: NovedadesAvivador;

  /** Click / Enter en la card, la franja o la fila. T4a: ABRE EL PANEL (ya no
   *  va a la rede) para todos los roles (Consulta = solo lectura, DP-22):
   *  - sin marca → `panel = { item, modo: 'accion' }`;
   *  - con marca (franja o fila marcada) → `panel = { item: marca.item, modo:
   *    'exito', resultado: marca.resultado }`.
   *  Con otro panel abierto, lo reemplaza. Con `panelPendiente` en true no hace nada.
   *  T4b: `modo` ('accion' por defecto; 'posponer' con la tecla P) solo vale
   *  SIN marca y con `puedeEscribir`; 'archivar' además solo en redes (no R4 ni
   *  obra). Con marca se ignora: siempre la caja ('exito') con `marca.resultado`
   *  (la franja pospuesta o archivada también abre su caja, DP-T4b-a). */
  abrir(item: AvivadorItem, modo?: ModoPedido): void;
  /** Guarda `FocoGuardadoV2` y navega a `/obras/:cc?rdt=:rdtId` (R4: `/obras/:cc`)
   *  con `state: { from: 'avivador' }`. En la Lista mide la fila (`data-lista-fila`).
   *  T4a: lo usan la tecla O, «Ir a la rede ↗» / «Ir a la obra {cc}» del pie
   *  del panel y `corregir`. Con el panel abierto también sirve (al volver, el
   *  panel está cerrado y el #8 restaura la card). */
  irARede(item: AvivadorItem): void;
  /** `me.rol !== 'viewer'`. T4a: sin permiso el panel es de solo lectura (sin
   *  form, sin Deshacer / Corregir; DP-22). T4b: la tecla P. */
  puedeEscribir: boolean;

  // ---- T4a: panel, marcas y Siguiente ----------------------------------

  /** Marcas locales por clave de entidad (Ajuste #2). Se limpian SOLO al
   *  desmontar la página: sobreviven a filtros, orden, búsqueda y «Actualizar». */
  marcas: ReadonlyMap<ClaveEntidad, MarcaLocal>;
  /** El panel abierto, o null. `panel.item.id` es el seleccionado: el slot va
   *  con `z-[45]` (arriba del velo), la card con `seleccionada` y la fila de
   *  la Lista con `seleccionadoId`. */
  panel: PanelAbierto | null;
  /** Cierra el panel (X, Esc, click en el velo, Cancelar, Cerrar). Con
   *  `panelPendiente` en true no hace nada (guarda contra cerrar con un POST en
   *  vuelo, §12.2; la misma que consulta el `onOpenChange` del Dialog). El descarte de
   *  un form con cambios lo pregunta el panel ANTES de llamarlo. Sin foco
   *  automático a la card de al lado: el foco vuelve al ítem (finalFocus). */
  cerrarPanel(): void;
  /** El form del panel registró algo (`onRegistrado` → `onExito`). Crea la
   *  marca `{ tipo: 'resuelta', ref: refDesdeResultado(r), sub:
   *  textosResultado(r).sub, posicion: índice de prioridad del ítem en la
   *  población compuesta, item, resultado: r }` por `claveEntidad(item)`,
   *  pasa el panel a `{ item, modo: 'exito', resultado: r }` y baja
   *  `panelPendiente` a false. SIN toast (§13 8.3: la caja verde confirma).
   *  El mosaico NO se reacomoda por la recarga que dispara la mutation (la foto
   *  sigue congelada); solo la card pasa a franja y su columna sube (FLIP). */
  registrarExito(item: AvivadorItem, r: ResultadoPaso): void;
  /** Deshacer de la franja, de la fila marcada o de la caja verde, según
   *  `marca.ref` (ver `MarcaRef`): monto → `useBorrarMonto`; hito →
   *  `useDeshacerHito`; chequeo y estado-redes → no hace nada (resuelve).
   *  - Bien: saca la marca, suma la clave a las «deshechas» (`propios`) y, si
   *    el panel está abierto en modo 'exito' sobre esa entidad, lo vuelve a
   *    'accion' (el form otra vez, M:451-458). Silencioso (§13 8.3).
   *  - posposición (T4b, Ajuste 21) → `useCancelarPosposicion(ref.id)`, sin
   *    exigir `rdtId` ni pedir el detalle de la rede: 404 / 409 = ya cancelada
   *    → éxito silencioso.
   *  - archivo (T4b, Ajuste 22) → `useArchivarRdt({ archivar: false })`: 400
   *    «no está archivada» con el detalle fresco sin archivar = éxito
   *    silencioso (`yaDeshecho` suma el archivo); 409 (regla 3) = toast rojo con
   *    el mensaje del backend y la marca queda.
   *  - Rechazo 404 / 409 / 422 (400 solo para archivo) con `yaDeshecho(ref,
   *    detalleFresco)` = ya deshecho en otro lado → éxito sin toast.
   *  - Otro error: toast rojo con el mensaje del backend; la marca queda.
   *  - Aprobación con `polizaAgregada`: después del deshacer, `PUT
   *    /api/rdts/:id {hitos:{polizaAplica:false}}`; si falla, toast rojo y la
   *    marca se va igual.
   *  - Antes de volver a 'accion' se pide `GET /api/rdts/:id` fresco (E1); la
   *    deshecha vuelve como card a su `posicion` hasta «Actualizar».
   *  - Con la marca fuera, el toast con «Deshacer» de esa marca (si sigue
   *    vivo) se descarta (`toast.dismiss`, Ajuste 23).
   *  - NUNCA rechaza. Un segundo llamado para la misma clave mientras el
   *    primero está en vuelo devuelve la misma promesa (guarda de doble clic;
   *    los botones además se deshabilitan mientras tanto). */
  deshacer(clave: ClaveEntidad): Promise<void>;
  /** «Corregir» de un chequeo (R1): `irARede(item)`, donde se corrige (DP-19). */
  corregir(item: AvivadorItem): void;
  /** Con el panel abierto: la próxima entidad SIN marca de `lista` (filtros y
   *  orden actuales), buscando por `claveEntidad` DESPUÉS de la del panel; si
   *  no hay, da la vuelta a la primera sin marca distinta de la del panel; si
   *  no queda ninguna, null («Siguiente» no aparece). Si la del panel no está
   *  en `lista` (filtrada), busca desde el principio. Sin panel, null. */
  siguiente: AvivadorItem | null;
  /** «Siguiente: cc · #nro →» (a mano, sin avance automático, §13 8.1): abre el
   *  panel en `siguiente` (modo 'accion') y lleva su slot o su fila a la vista
   *  con `scrollIntoView({ block: 'nearest' })` en un rAF. Sin `siguiente`, nada. */
  irASiguiente(): void;
  /** Hay un POST del form del panel en vuelo (lo reporta `PanelAvivadorAccion`
   *  con `onPendingChange`). Con true, `cerrarPanel` y `abrir` no hacen nada y
   *  el Dialog ignora el cierre por Esc / velo / X. Lo baja a false
   *  `registrarExito` (el form se desmonta en el mismo render) y el cleanup
   *  del efecto de `PanelAvivadorAccion` al desmontarse (nunca queda trabado).
   *  El Deshacer NO lo usa (cada botón maneja su propio «Deshaciendo…»). */
  panelPendiente: boolean;
  setPanelPendiente(pendiente: boolean): void;

  /** Input del buscador (tecla /, destino del foco de MetaFiltros sin chips). */
  buscadorRef: RefObject<HTMLInputElement | null>;
  /** Popover «Atajos de teclado» de la meta-row: controlado, lo abre también la tecla ?. */
  atajosAbierto: boolean;
  setAtajosAbierto(abierto: boolean): void;
  /** Popover «Filtros»: controlado, para que abrir Atajos (?) lo cierre y el
   *  listener sepa que hay un popup abierto. */
  popoverAbierto: boolean;
  setPopoverAbierto(abierto: boolean): void;

  // ---- T4b: Posponer, Archivar y «N pospuestas» ------------------------

  /** Pie «Posponer…» / «Archivar…» y «Cancelar» de esos modos (vuelve a
   *  'accion'). Solo con el panel abierto en 'accion', 'posponer' o 'archivar',
   *  sin `panelPendiente` y con `puedeEscribir`; 'archivar' solo en redes. No
   *  cierra. El descarte de un form con cambios lo pregunta el panel ANTES de
   *  llamarlo (Ajuste 20). */
  setModo(modo: ModoPedido): void;
  /** Modo 'posponer' → «Posponer hasta dd/mm». Hace el POST /api/avivador/posponer
   *  ACÁ (Ajuste 20) con `{ entidadTipo, rdtId, cc, hasta, nota }` y sube
   *  `panelPendiente` mientras tanto.
   *  - Bien: marca `{ tipo: 'pospuesta', ref: { kind: 'posposicion', id,
   *    hasta }, sub: textosResultado(r).sub, posicion, item, resultado: r }` con
   *    r = `{ tipo: 'posposicion', id, hasta, nota }` (en el acto, como
   *    `registrarExito`), saca la clave de las deshechas, CIERRA el panel, baja
   *    `panelPendiente` y toast.success(`Pospuesta hasta el ${fDdMm(hasta)}.`,
   *    { duration: 10000, action: «Deshacer» }) cuyo Deshacer solo actúa si la
   *    marca vigente sigue siendo ESA posposición (mismo `id`; Ajuste 23).
   *    Resuelve true.
   *  - Error: toast.error con el mensaje del backend (422 nota / fecha, 404 «no
   *    está en la población vigente»), baja `panelPendiente`, el panel queda
   *    abierto en 'posponer'. Resuelve false. NUNCA rechaza. */
  posponer(item: AvivadorItem, datos: { hasta: string; nota: string }): Promise<boolean>;
  /** Modo 'archivar' → «Archivar». Hace el POST /api/rdts/:id/archivar ACÁ con
   *  `{ nota }` (null si vacía) y sube `panelPendiente` mientras tanto.
   *  - Bien: marca `{ tipo: 'archivada', ref: { kind: 'archivo' }, … }` con r =
   *    `{ tipo: 'archivo', nota }`, cierra, baja `panelPendiente` y
   *    toast.success(`Rede #${nro} archivada: sale del Avivador.`, { duration:
   *    10000, action: «Deshacer» }). Resuelve true.
   *  - Error: toast.error con el mensaje del backend. Resuelve false. NUNCA
   *    rechaza. Solo redes (`rdtId` no nulo). */
  archivar(item: AvivadorItem, nota: string | null): Promise<boolean>;
  /** «N pospuestas» canceló la posposición `id` (ya lo hizo el popover): si
   *  hay una marca con `ref.kind === 'posposicion' && ref.id === id`, sale como
   *  un Deshacer que salió bien (la card vuelve a su lugar; toast de esa marca
   *  descartado), sin llamar al backend. Sin marca, nada (DP-T4b-c). */
  posposicionCancelada(id: number): void;

  // ---- T5: Comentario local (§13 7.1/8.3; plan T5 A3) -------------------

  /** Comentarios agregados desde «Comentar» sin esperar a «Actualizar»
   *  (`comentarioLocal`). Map por `claveEntidad` (Ajuste #2), igual
   *  permanencia que `marcas` (Ajuste #6: sobrevive a filtros, orden,
   *  búsqueda y «Actualizar»; se limpia SOLO al desmontar la página). La
   *  card y la fila de la Lista pintan
   *  `comentariosLocales.get(claveEntidad(item))` en vez de
   *  `item.ultimoComentario` / `item.comentariosCount` cuando está presente
   *  — SIN pasar a franja ni cambiar de rama o de `posicion` (no es una
   *  marca): solo se recoloca su columna si el alto cambió (FLIP, DP-4). */
  comentariosLocales: ReadonlyMap<ClaveEntidad, ComentarioLocal>;
  /** `PanelAvivadorActividad` ya hizo el POST (`useCrearComentario`), armó
   *  `c: AvivadorUltimoComentario` y mostró `toast.success` — esto SOLO
   *  actualiza el estado local (sin toast propio, §13 8.3): crea o actualiza
   *  `comentariosLocales.get(claveEntidad(item))` con `{ ultimoComentario: c,
   *  delta: (anterior?.delta ?? 0) + 1 }` (sucesivos «Comentar» sobre la
   *  misma entidad ACUMULAN, nunca reinician `delta` a 1). No toca `marcas`
   *  ni abre o cierra el panel. */
  comentarioLocal(item: AvivadorItem, c: AvivadorUltimoComentario): void;
}

// ---------------------------------------------------------------------------
// Props de los componentes compartidos entre ejecutores
// ---------------------------------------------------------------------------

/** `Mosaico` (E2, mosaico.tsx). Técnica DP-4: posicionamiento absoluto de
 *  cada slot sobre UN contenedor en orden de prioridad. El contenedor NO crea
 *  stacking context (sin transform, opacity, filter ni isolation). */
export interface MosaicoProps {
  /** En el orden a pintar. El Mosaico reparte cuando cambian los ids (su
   *  `idsKey` lo calcula adentro), no cuando cambia `layoutKey`. */
  items: AvivadorItem[];
  /** Solo decide si el reparto nuevo va con FLIP: si cambió junto con los ids
   *  → FLIP (220 ms, cubic-bezier(0.22,1,0.36,1); las que entran, fade de
   *  180 ms; salto > 0,6 × innerHeight → fade). Nunca hay FLIP en el primer
   *  layout, en `document.fonts.ready` ni en el resize. Nada con reduced-motion. */
  layoutKey: string;
  /** T4a: ids (`item.id`) de los ítems de `items` que se pintan como franja
   *  (su clave tiene marca). La COLUMNA la decide el alto completo guardado
   *  (resolver no cambia columnas); el alto real (la franja) decide el top, y
   *  las de abajo de esa columna suben con FLIP (`recolocar`, Ajuste #4). */
  compactos?: ReadonlySet<string>;
  /** T4a: `estado.panel?.item.id ?? null`. El slot seleccionado lleva solo
   *  `z-[45]` (SIN `relative`: el slot es absoluto) y queda arriba del velo (z-40). */
  seleccionadoId?: string | null;
  /** Cada ítem se envuelve en su slot; `renderItem` devuelve la card. */
  renderItem: (item: AvivadorItem) => ReactNode;
  /** Una vez, cuando todos los slots tienen posición y se ven (restaurar el #8). */
  onPrimerLayout?: () => void;
}

/** `CardMosaico` (E3, card-mosaico.tsx): un solo `<article tabIndex={0}
 *  data-avivador-item={item.id} data-mosaico-card aria-label={ariaLabelCard(…)}>`.
 *  El click llama a `onAbrir(item)`. NO maneja teclas: Enter lo maneja el
 *  listener global de la página. T4a: el aria-label pasa a
 *  `ariaLabelCard(item, ctx, 'panel')` («… Abre el panel para registrar
 *  entrega.»), porque click y Enter abren el panel. */
export interface CardMosaicoProps {
  item: AvivadorItem;
  ctx: CtxAvivador;
  /** T4a: panel abierto sobre ella (borde primary/55 + ring fino + sombra
   *  elevada, §13 6.2), arriba del velo. */
  seleccionada?: boolean;
  onAbrir: (item: AvivadorItem) => void;
}

/** `ControlesAvivador` (F4, controles-avivador.tsx): una fila flex-wrap gap-2.5,
 *  todo h-9 (44 en táctil). Buscador controlado (placeholder y aria-label
 *  EXACTOS de §13 1.1: «Buscar obra, rede, cliente o comentario… (Enter agrega
 *  como filtro)» / «Buscar en el Avivador; Enter convierte el texto en un
 *  filtro»; Enter → `estado.enterBuscador(valor)`; Esc → `accionEscBuscador(valor,
 *  { blurSiVacio: true })`), `FiltrosAvivadorPopover` y el toggle Mosaico |
 *  Lista (RadioGroup aria-label «Vista», íconos + texto, SIN conteos; el
 *  activo en `surface-2` con ring interior subtle, no naranja). */
export interface ControlesAvivadorProps {
  estado: EstadoAvivador;
}

/** `FiltrosAvivadorPopover` (F4, filtros-avivador-popover.tsx): el
 *  `FiltrosPopover` de components/filtros con `ariaLabel` «Filtros del
 *  Avivador», `pie` «<b>N</b> redes con estos filtros» (N = `lista.length`) y
 *  las secciones, en orden: Mostrar (segmentado Vivas/Olvidadas/Todas con
 *  `facetas.vida`) · Ramas (múltiple, `RAMA_ORDEN_CICLO`, conteo n + total de
 *  `facetas.ramas`) · Control RDT (único, chips: «Todo el equipo» =
 *  `TODO_EL_EQUIPO` + `encargadas` con su nombre y `facetas.encargadas`) · Tipo
 *  (múltiple, Provisoria/Definitiva, sin conteo) · [separada] Orden (único,
 *  variante 'lista': Prioridad «la cola del Avivador» · Más demoradas primero
 *  «por días» · Por obra «agrupa cada obra» · Por cliente «agrupa cada
 *  cliente»; `valor` null si el orden es rede, estado o encargada). Unidad:
 *  rede/redes. */
export interface FiltrosAvivadorPopoverProps {
  estado: EstadoAvivador;
}

/** `MetaAvivador` (F4, meta-avivador.tsx): `MetaFiltros` de components/filtros
 *  con el conteo («Mostrando <b>N</b> vivas de <b>M</b> redes» y variantes,
 *  §13 1.2), los chips del estado (`focoSinChipsRef = estado.buscadorRef`) y
 *  a la derecha «N pospuestas», «Umbrales» y «?» (atajos, ahora con L). */
export interface MetaAvivadorProps {
  estado: EstadoAvivador;
}

/** `NovedadesAviso` (F4, novedades-aviso.tsx): arriba del Mosaico o de la
 *  Lista, solo con `count > 0`. Caja `role="status"` borde info/30, fondo
 *  info/7, radio 10, 13 px; ícono (i) + `<b>{titulo}</b>` + `textoOtros` si lo
 *  hay + botón «Actualizar» (h-[30px], 44 en táctil, borde y texto info).
 *  Entrada de 160 ms con motion-safe. */
export interface NovedadesAvisoProps {
  novedades: NovedadesAvivador;
}

/** `TablaAvivador` (F3, tabla-avivador.tsx): la Lista sobre el data-grid de
 *  ReUI (components/reui/data-grid, sin editarlo; se tematiza con
 *  `tableClassNames`). `useTable` + `DataGrid` + `DataGridColumnHeader`, SIN
 *  paginación (con la paginación por defecto mostraría 10 filas) y con
 *  `manualSorting`: `state.sorting` sale de `orden`/`ordenDir` y
 *  `onSortingChange` llama a `onClicColumna` (nunca el ciclo asc → desc → sin
 *  orden de ReUI). Columnas: Rede · Obra · Rama · Demora · Último comentario ·
 *  Estado · Cliente · Control RDT; ordenan Rede ('rede'), Obra ('obra'),
 *  Demora ('dias'), Estado ('estado'), Cliente ('cliente') y Control RDT
 *  ('encargada'), con `aria-sort`. Compacta si SU CONTENEDOR mide < 1000 px
 *  (DP-18, medido con ResizeObserver): Rede · Obra · Rama · Demora · Cliente ·
 *  Control RDT. Click en la fila → `onAbrir(item)`; la celda Rede es el
 *  `<button data-avivador-item>` (contrato DOM). `key` de fila = `item.id`. */
export interface TablaAvivadorProps {
  /** Ya filtrada y ordenada (`estado.lista`). */
  items: AvivadorItem[];
  ctx: CtxAvivador;
  orden: OrdenClave;
  ordenDir: OrdenDir;
  onClicColumna: (c: OrdenClave) => void;
  /** T4a: fila con el panel abierto (fondo primary/8 %): `estado.panel?.item.id ?? null`.
   *  Si WebKit pinta bien un `<tr>` con `relative z-[45]`, va arriba del velo;
   *  si no, se acepta el velo encima (plan P4). */
  seleccionadoId?: string | null;
  onAbrir: (item: AvivadorItem) => void;
  /** T4a (DP-24): marcas por clave de entidad (`estado.marcas`). La fila cuya
   *  `claveEntidad(item)` tiene marca va con fondo `success/6`; la celda Rama
   *  muestra ícono de check + «Resuelta»; la celda Último comentario, el
   *  `marca.sub` + el botón que diga `accionFranja(marca.ref)` («Deshacer» /
   *  «Corregir» / nada). En la compacta (sin Último comentario) el `sub` y el
   *  botón van en la celda Rama. El botón hace `stopPropagation` (no abre el
   *  panel) y muestra «Deshaciendo…» deshabilitado mientras la promesa no
   *  termina. Click en la fila marcada → `onAbrir(item)` (panel en 'exito').
   *  Omitido = sin marcas (como T3b). */
  marcas?: ReadonlyMap<ClaveEntidad, MarcaLocal>;
  /** T4a: `estado.deshacer`. Obligatorio si `marcas` viene con alguna marca de Deshacer. */
  onDeshacer?: (clave: ClaveEntidad) => Promise<void>;
  /** T4a: `estado.corregir`. */
  onCorregir?: (item: AvivadorItem) => void;
  /** T4a: `estado.puedeEscribir`. Sin permiso, la fila marcada no muestra
   *  botón (no puede pasar: Consulta no marca; defensa). Default true. */
  puedeEscribir?: boolean;
}
